import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics, trackUserAction } from '../services/analytics';
import { EventActions } from '../config/analytics';

/**
 * Custom hook for analytics tracking
 * Automatically tracks page views and provides tracking functions
 */
export const useAnalytics = () => {
  const location = useLocation();

  // Track page views automatically
  useEffect(() => {
    analytics.trackPageView(location.pathname + location.search);
  }, [location]);

  return {
    // Core analytics functions
    trackEvent: analytics.trackEvent.bind(analytics),
    trackPageView: analytics.trackPageView.bind(analytics),
    trackConversion: analytics.trackConversion.bind(analytics),
    trackError: analytics.trackError.bind(analytics),
    trackTiming: analytics.trackTiming.bind(analytics),
    setUserProperties: analytics.setUserProperties.bind(analytics),
    
    // Consent management
    hasConsent: analytics.hasConsent.bind(analytics),
    grantConsent: analytics.grantConsent.bind(analytics),
    revokeConsent: analytics.revokeConsent.bind(analytics),
    
    // Convenience functions for common events
    trackUserSignup: (method?: string) => trackUserAction(EventActions.SIGN_UP, method),
    trackUserLogin: (method?: string) => trackUserAction(EventActions.LOGIN, method),
    trackUserLogout: () => trackUserAction(EventActions.LOGOUT),
    
    trackBookingInitiated: (interviewerId?: string) => {
      analytics.trackEvent({
        category: 'Booking',
        action: EventActions.BOOKING_INITIATED,
        label: interviewerId
      });
    },
    
    trackBookingCompleted: (bookingId?: string, amount?: number) => {
      analytics.trackEvent({
        category: 'Booking',
        action: EventActions.BOOKING_COMPLETED,
        label: bookingId,
        value: amount
      });
      
      // Also track as conversion
      analytics.trackConversion('booking_completed', amount);
    },
    
    trackPaymentCompleted: (amount: number, currency = 'USD') => {
      analytics.trackEvent({
        category: 'Payment',
        action: EventActions.PAYMENT_COMPLETED,
        value: amount,
        customParameters: { currency }
      });
      
      // Track as conversion
      analytics.trackConversion('payment_completed', amount, currency);
    },
    
    trackProfileCompleted: (userRole?: string) => {
      analytics.trackEvent({
        category: 'Profile',
        action: EventActions.PROFILE_COMPLETED,
        label: userRole
      });
      
      // Track as conversion
      analytics.trackConversion('profile_completed');
    }
  };
};
